import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import type { Customer, EventStatus } from "@attendik/types/admin-schemas";

import { api } from "../api/client.js";
import { isApiError } from "../api/errors.js";
import { PageHeader } from "../components/AppLayout.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../components/ui/dialog.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { formatRange } from "../lib/format.js";
import { fromDateTimeLocal } from "../lib/format.js";
import { useAsync } from "../lib/useAsync.js";
import { useScope } from "../store/auth.js";
import { StatusBadge, eventStatusVariant } from "./shared/pills.js";

interface ListData {
  events: Awaited<ReturnType<typeof api.listEvents>>;
  customers: Customer[];
}

/** Events list with filters + create flow (build plan P2-T7). */
export function EventsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { isPortalAdmin, customerId } = useScope();
  const [statusFilter, setStatusFilter] = useState<EventStatus | "">("");
  const [customerFilter, setCustomerFilter] = useState<string>("");
  const [creating, setCreating] = useState(false);

  const { data, loading, error, reload } = useAsync<ListData>(async () => {
    const events = await api.listEvents({
      status: statusFilter || undefined,
      customer_id: isPortalAdmin && customerFilter ? customerFilter : undefined
    });
    const customers = isPortalAdmin ? await api.listCustomers() : [];
    return { events, customers };
  }, [statusFilter, customerFilter, isPortalAdmin]);

  const customerName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of data?.customers ?? []) {
      map.set(c.id, c.name);
    }
    return map;
  }, [data?.customers]);

  return (
    <>
      <PageHeader
        title="Events"
        description="Events within your scope."
        actions={<Button onClick={() => setCreating(true)}>Create event</Button>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Label htmlFor="status-filter" className="text-muted-foreground">
            Status
          </Label>
          <Select
            id="status-filter"
            className="w-36"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as EventStatus | "")}
          >
            <option value="">All</option>
            <option value="ACTIVE">Active</option>
            <option value="CLOSED">Closed</option>
          </Select>
        </div>
        {isPortalAdmin ? (
          <div className="flex items-center gap-2">
            <Label htmlFor="customer-filter" className="text-muted-foreground">
              Customer
            </Label>
            <Select
              id="customer-filter"
              className="w-52"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
            >
              <option value="">All customers</option>
              {data?.customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
      </div>

      <Card>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !data || data.events.length === 0 ? (
          <EmptyState
            title="No events"
            description="Create an event to begin managing attendees."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                {isPortalAdmin ? <TableHead>Customer</TableHead> : null}
                <TableHead>Schedule</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.events.map((event) => (
                <TableRow
                  key={event.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      {event.name}
                      {event.meetpoint_polling_enabled ? (
                        <span
                          className="inline-flex h-5 items-center rounded-md border border-amber-400/40 bg-amber-100/40 px-1.5 text-[0.625rem] font-semibold uppercase tracking-wider text-amber-700"
                          title="Meetpoint polling is on — extra load on the MP servers"
                          data-testid={`event-polling-on-${event.id}`}
                        >
                          MP-poll
                        </span>
                      ) : null}
                    </span>
                  </TableCell>
                  {isPortalAdmin ? (
                    <TableCell className="text-muted-foreground">
                      {customerName.get(event.customer_id) ?? "—"}
                    </TableCell>
                  ) : null}
                  <TableCell className="text-muted-foreground">
                    {formatRange(event.active_from, event.active_to)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {event.capacity ?? "—"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge variant={eventStatusVariant(event.status)}>
                      {event.status}
                    </StatusBadge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {creating ? (
        <CreateEventDialog
          customers={data?.customers ?? []}
          ownCustomerId={customerId}
          isPortalAdmin={isPortalAdmin}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            navigate(`/events/${id}`);
          }}
        />
      ) : null}
    </>
  );
}

function CreateEventDialog({
  customers,
  ownCustomerId,
  isPortalAdmin,
  onClose,
  onCreated
}: {
  customers: Customer[];
  ownCustomerId: string | null;
  isPortalAdmin: boolean;
  onClose: () => void;
  onCreated: (eventId: string) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(
    isPortalAdmin ? (customers[0]?.id ?? "") : (ownCustomerId ?? "")
  );
  const [activeFrom, setActiveFrom] = useState("");
  const [activeTo, setActiveTo] = useState("");
  const [capacity, setCapacity] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const customerForEvent = isPortalAdmin ? selectedCustomer : ownCustomerId;
    if (!customerForEvent) {
      setError("A customer is required.");
      return;
    }
    if (new Date(activeTo) <= new Date(activeFrom)) {
      setError("The end time must be after the start time.");
      return;
    }
    setSubmitting(true);
    try {
      const event = await api.createEvent({
        customer_id: customerForEvent,
        name: name.trim(),
        active_from: fromDateTimeLocal(activeFrom),
        active_to: fromDateTimeLocal(activeTo),
        capacity: capacity ? Number(capacity) : null,
        webhook_url: webhookUrl.trim() || null
      });
      onCreated(event.id);
    } catch (err) {
      if (isApiError(err) && err.code === "CUSTOMER_DISABLED") {
        setError("That customer is disabled — events cannot be created for it.");
      } else if (isApiError(err) && err.status === 400) {
        setError(err.message);
      } else {
        setError("Could not create the event. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="create-event-title">
      <DialogHeader
        id="create-event-title"
        title="Create event"
        description="Set the schedule and optional capacity."
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {isPortalAdmin ? (
            <div className="space-y-1.5">
              <Label htmlFor="event-customer">Customer</Label>
              <Select
                id="event-customer"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                required
              >
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="event-name">Name</Label>
            <Input
              id="event-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="event-from">Active from</Label>
              <Input
                id="event-from"
                type="datetime-local"
                value={activeFrom}
                onChange={(e) => setActiveFrom(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="event-to">Active to</Label>
              <Input
                id="event-to"
                type="datetime-local"
                value={activeTo}
                onChange={(e) => setActiveTo(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="event-capacity">Capacity (optional)</Label>
            <Input
              id="event-capacity"
              type="number"
              min={0}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="event-webhook">Webhook URL (optional)</Label>
            <Input
              id="event-webhook"
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://monitor.example/hook"
            />
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
